import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFormatCurrency } from "@/context/CountryContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Star, MapPin, Phone, CheckCircle2, MessageSquare } from "lucide-react";

export default function ProviderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const formatCurrency = useFormatCurrency();
  const [p, setP] = useState(null);
  const [date, setDate] = useState(new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [quote, setQuote] = useState({ service: "", description: "", preferred_date: "" });
  const [openQuote, setOpenQuote] = useState(false);

  useEffect(() => { api.get(`/providers/${id}`).then((r) => setP(r.data)); }, [id]);
  useEffect(() => { if (p) api.get(`/providers/${id}/availability`, { params: { date } }).then((r) => setSlots(r.data.slots)); }, [id, date, p]);

  const requireClient = () => {
    if (!user) { toast.error("Connectez-vous pour continuer"); navigate("/login"); return false; }
    if (user.role !== "client") { toast.error("Réservé aux comptes client"); return false; }
    return true;
  };

  const submitQuote = async () => {
    if (!requireClient()) return;
    try {
      await api.post("/quote-requests", { provider_id: id, ...quote });
      toast.success("Demande de devis envoyée !");
      setOpenQuote(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const book = async () => {
    if (!requireClient()) return;
    if (!slot) { toast.error("Choisissez un créneau"); return; }
    try {
      const { data } = await api.post("/appointments", { provider_id: id, date, slot, service: p.profession });
      toast.success("RDV confirmé !");
      const tx = await api.post("/payments/initiate", { kind: "prestation", amount: p.base_rate || 0, reference_id: data.appt_id });
      navigate(`/paiement/${tx.data.tx_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!p) return <div className="text-center py-20 text-muted-foreground">Chargement…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <div className="rounded-xl overflow-hidden border border-border">
            <img src={p.photo_url} alt={p.name} className="w-full h-56 object-cover" />
          </div>
          <div className="mt-4 flex items-center gap-3">
            <h1 className="font-display text-2xl font-bold text-secondary">{p.name}</h1>
            <Badge variant="secondary">{p.profession}</Badge>
            <span className="text-sm flex items-center gap-1 text-amber-500"><Star className="w-4 h-4 fill-amber-500" />{p.rating}</span>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mt-2">
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{p.city} · {(p.zones || []).join(", ")}</span>
            {p.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{p.phone}</span>}
          </div>
          <p className="mt-4 text-secondary/80">{p.bio}</p>
          <h3 className="font-semibold text-secondary mt-6 mb-2">Services proposés</h3>
          <div className="flex flex-wrap gap-2">
            {(p.services || []).map((s) => <span key={s} className="px-3 py-1.5 rounded-full bg-muted text-sm flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-accent" />{s}</span>)}
          </div>
        </div>

        <div className="md:sticky md:top-20 h-fit">
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="text-sm text-muted-foreground">Tarif de base</div>
            <div className="font-display text-2xl font-bold text-primary mb-4">{p.base_rate ? formatCurrency(p.base_rate) : "Sur devis"}</div>
            <Tabs defaultValue="rdv">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="rdv" data-testid="tab-rdv">Réserver</TabsTrigger>
                <TabsTrigger value="devis" data-testid="tab-devis">Devis</TabsTrigger>
              </TabsList>
              <TabsContent value="rdv" className="space-y-3 mt-3">
                <div><Label>Date</Label><Input type="date" data-testid="appt-date" value={date} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} /></div>
                <div className="grid grid-cols-3 gap-2">
                  {slots.map((s) => (
                    <button key={s.time} disabled={!s.available} data-testid={`slot-${s.time}`}
                      onClick={() => setSlot(s.time)}
                      className={`text-xs py-2 rounded-md border transition-colors ${slot === s.time ? "bg-primary text-white border-primary" : s.available ? "border-border hover:border-primary" : "opacity-40 cursor-not-allowed line-through"}`}>
                      {s.time}
                    </button>
                  ))}
                </div>
                <Button data-testid="book-btn" className="w-full bg-primary hover:bg-primary/90" onClick={book}>Confirmer & payer</Button>
              </TabsContent>
              <TabsContent value="devis" className="mt-3">
                <Dialog open={openQuote} onOpenChange={setOpenQuote}>
                  <DialogTrigger asChild><Button data-testid="open-quote" variant="outline" className="w-full">Demander un devis</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Demande de devis</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <div><Label>Service</Label><Input data-testid="quote-service" value={quote.service} onChange={(e) => setQuote({ ...quote, service: e.target.value })} placeholder="Ex: Réparation fuite" /></div>
                      <div><Label>Détails</Label><Textarea data-testid="quote-desc" value={quote.description} onChange={(e) => setQuote({ ...quote, description: e.target.value })} placeholder="Décrivez votre besoin" /></div>
                      <div><Label>Date souhaitée</Label><Input type="date" value={quote.preferred_date} onChange={(e) => setQuote({ ...quote, preferred_date: e.target.value })} /></div>
                    </div>
                    <DialogFooter><Button data-testid="submit-quote" className="bg-primary hover:bg-primary/90" onClick={submitQuote}>Envoyer</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
              </TabsContent>
            </Tabs>
            {user && user.role === "client" && (
              <Button variant="ghost" className="w-full mt-2" data-testid="msg-provider" onClick={() => navigate(`/messages?to=${p.owner_id}`)}>
                <MessageSquare className="w-4 h-4 mr-2" />Envoyer un message
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
